import type { SupportedLocale } from "./locales";
import moment from "moment";
import { watch } from "vue";
import { createI18n } from "vue-i18n";
import { loadLocaleCatalog } from "./localeMessages";
import { FALLBACK_LOCALE, resolveSupportedLocale } from "./locales";
import { localeState } from "./localeState";
import { loadMomentLocale } from "./momentLocales";
import { pluralRules } from "./pluralRules";

export const locale = localeState;

const currencyFormat = {
  currency: {
    style: "currency",
    currency: "EUR",
  },
} as const;

export const i18n = createI18n({
  legacy: false,
  locale: FALLBACK_LOCALE,
  fallbackLocale: FALLBACK_LOCALE,
  messages: {},
  pluralRules,
});

const loadedLocales = new Set<SupportedLocale>();
const pendingLocales = new Map<SupportedLocale, Promise<void>>();

function loadMessages(code: SupportedLocale): Promise<void> {
  if (loadedLocales.has(code)) {
    return Promise.resolve();
  }
  const pending = pendingLocales.get(code);
  if (pending) {
    return pending;
  }
  const task = loadLocaleCatalog(code)
    .then((messages) => {
      i18n.global.setLocaleMessage(code, messages);
      i18n.global.setNumberFormat(code, currencyFormat);
      loadedLocales.add(code);
    })
    .finally(() => pendingLocales.delete(code));
  pendingLocales.set(code, task);
  return task;
}

/**
 * Loads a catalog without switching to it, together with the Dutch fallback that holds the keys other
 * locales still miss. Needed before `t(key, params, { locale })` lookups in a non-active locale.
 */
export async function loadLocale(code: string | null | undefined): Promise<SupportedLocale> {
  const next = resolveSupportedLocale(code);
  await Promise.all([loadMessages(next), loadMessages(FALLBACK_LOCALE)]);
  return next;
}

let latestRequest = 0;

/** Switches only after the catalog and moment's locale have loaded; a slower earlier request never overrides a later one. */
export async function setLocale(code: string | null | undefined): Promise<SupportedLocale> {
  const request = ++latestRequest;
  const resolved = resolveSupportedLocale(code);
  const [next] = await Promise.all([loadLocale(resolved), loadMomentLocale(resolved)]);
  if (request === latestRequest) {
    i18n.global.locale.value = next;
    moment.locale(next);
    // The page language lets screen readers pick the right voice and lets the browser hyphenate long words.
    if (typeof document !== "undefined") {
      document.documentElement.lang = next;
    }
  }
  return next;
}

function reportLoadFailure(error: unknown): void {
  console.error("Failed to load translations", error);
}

/** Resolves once the stored locale is active. App entries await it before mounting. */
export const localeReady: Promise<void> = setLocale(localeState.value).then(() => undefined, reportLoadFailure);

watch(localeState, (value) => {
  setLocale(value).catch(reportLoadFailure);
});

export default i18n;

export function useI18n() {
  return i18n.global;
}
